import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { UserCircle2, Mail, Camera, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ProfileSection() {
  const { t } = useI18n();
  const { user, setUser, refresh } = useAuth();
  const fileRef = useRef(null);
  const [editName, setEditName] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [newEmail, setNewEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);

  const isLocal = (user?.auth_provider || "local") === "local";

  const onPickAvatar = () => fileRef.current?.click();
  const onAvatarChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setSaving(true);
    try {
      const { data } = await api.post("/auth/profile/picture", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser(data);
      toast.success(t.security.profile.avatarSaved);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, t.security.profile.avatarError));
    } finally {
      setSaving(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveName = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      setUser(data);
      setEditName(false);
      toast.success(t.security.profile.nameSaved);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveEmail = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-email", {
        password: pwd, new_email: newEmail.trim().toLowerCase(),
      });
      setUser(data);
      setEditEmail(false);
      setPwd("");
      await refresh();
      toast.success(t.security.profile.emailSaved);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, t.security.profile.emailError));
    } finally {
      setSaving(false);
    }
  };

  const initial = (user?.first_name || user?.email || "?").charAt(0).toUpperCase();
  const _apiOrigin =
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    process.env.REACT_APP_BACKEND_URL;
  const avatarUrl = user?.picture && user.picture.startsWith("/api/")
    ? (_apiOrigin + user.picture)
    : user?.picture;

  return (
    <section className="ag-card p-7" data-testid="settings-profile-section">
      <div className="flex items-center gap-2 mb-5">
        <UserCircle2 className="h-4 w-4 text-slate-500" />
        <h2 className="font-serif text-2xl text-ag-ink">{t.security.profile.title}</h2>
      </div>

      <div className="flex items-start gap-6 mb-7">
        <button
          type="button" onClick={onPickAvatar}
          data-testid="settings-avatar-btn"
          className="group relative h-20 w-20 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-200 overflow-hidden shrink-0"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="avatar" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full flex items-center justify-center font-serif text-3xl text-ag-ink">
              {initial}
            </div>
          )}
          <div className="absolute inset-0 bg-ag-ink/0 group-hover:bg-ag-ink/40 transition-colors flex items-center justify-center text-white opacity-0 group-hover:opacity-100">
            <Camera className="h-5 w-5" strokeWidth={1.5} />
          </div>
        </button>
        <input
          ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatarChange}
          data-testid="settings-avatar-input"
        />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t.security.profile.name}</div>
          {!editName ? (
            <div className="flex items-center gap-3">
              <div className="font-serif text-xl text-ag-ink">{user?.first_name} {user?.last_name}</div>
              <button
                onClick={() => setEditName(true)} data-testid="settings-edit-name"
                className="text-xs text-slate-500 hover:text-ag-ink inline-flex items-center gap-1"
              >
                <Pencil className="h-3 w-3" /> {t.security.profile.edit}
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2 mt-1">
              <Input
                value={firstName} onChange={(e) => setFirstName(e.target.value)}
                placeholder={t.auth.firstName} data-testid="settings-name-first" className="h-10 bg-white"
              />
              <Input
                value={lastName} onChange={(e) => setLastName(e.target.value)}
                placeholder={t.auth.lastName} data-testid="settings-name-last" className="h-10 bg-white"
              />
              <div className="sm:col-span-2 flex items-center gap-2">
                <Button size="sm" onClick={saveName} disabled={saving} data-testid="settings-save-name" className="bg-ag-ink hover:bg-slate-800 h-9 rounded-lg">
                  {t.common.save}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditName(false); setFirstName(user?.first_name || ""); setLastName(user?.last_name || ""); }} className="h-9 rounded-lg">
                  {t.common.cancel}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-slate-100 pt-6 grid sm:grid-cols-2 gap-6 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 inline-flex items-center gap-1.5">
            <Mail className="h-3 w-3" /> {t.auth.email}
          </div>
          <div className="font-serif text-lg text-ag-ink break-all">{user?.email}</div>
          {isLocal && (
            <button
              onClick={() => { setEditEmail(true); setNewEmail(user?.email || ""); }}
              data-testid="settings-edit-email"
              className="text-xs text-slate-500 hover:text-ag-ink inline-flex items-center gap-1 mt-2"
            >
              <Pencil className="h-3 w-3" /> {t.security.profile.changeEmail}
            </button>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" /> {t.security.profile.authMethod}
          </div>
          <div className="font-serif text-lg text-ag-ink capitalize">{user?.auth_provider || "local"}</div>
          {!isLocal && (
            <div className="text-[11px] text-slate-500 mt-1">{t.security.profile.googleManaged}</div>
          )}
        </div>
      </div>

      <Dialog open={editEmail} onOpenChange={setEditEmail}>
        <DialogContent data-testid="settings-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{t.security.profile.changeEmail}</DialogTitle>
            <DialogDescription>{t.security.profile.changeEmailSub}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.security.profile.newEmail}</Label>
              <Input
                type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                data-testid="settings-email-new" className="mt-1.5 h-11 bg-white"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.security.profile.confirmPwd}</Label>
              <Input
                type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                data-testid="settings-email-pwd" className="mt-1.5 h-11 bg-white"
                placeholder={t.security.profile.confirmPwdPlaceholder}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditEmail(false)}>{t.common.cancel}</Button>
            <Button onClick={saveEmail} disabled={saving || !newEmail || !pwd} data-testid="settings-email-save" className="bg-ag-ink hover:bg-slate-800">
              {t.common.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
